/**
 * HomeownerInspirePage
 * Image-first gallery over public Finishd projects. Every card is a preview
 * photo of a real project (issue #378 replaced the static feed). Scope,
 * search and room are all filters the server applies, and they live in the
 * address. The room chips list the rooms of every public project the other
 * filters leave (`facets.rooms`), not only those on the loaded pages, plus the
 * chosen room when it is not among them, so the selected room stays visible as
 * a chip while the other filters exclude it. Only the Saved view, a complete
 * list, is narrowed by the search box in the browser.
 */

import { ArrowRight, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SkeletonRows } from "@/components/ui/skeleton";
import type { PublicProjectSummary } from "@/features/marketplace/services/marketplace-api.service";
import { fetchPublicProjects } from "@/features/marketplace/services/marketplace-api.service";
import { StudioButton } from "@/features/studio/theme";
import {
	InspireFilterBar,
	InspireHero,
	type InspireRoom,
	InspireRoomCard,
	type InspireScope,
	type InspireView,
	SearchBar,
} from "../components";
import { useRecentSearches } from "../hooks/use-recent-searches";
import { useSavedIdeas } from "../hooks/use-saved-ideas";

const PAGE_LIMIT = 40;

/** How long the typing must stop before the page searches. */
export const SEARCH_DEBOUNCE_MS = 300;

const ASPECT_CYCLE = [
	"aspect-[3/4]",
	"aspect-[4/5]",
	"aspect-[1/1]",
	"aspect-[4/3]",
] as const;

interface InspireCard extends InspireRoom {
	createdAt: string;
}

function toCards(projects: PublicProjectSummary[]): InspireCard[] {
	return projects.flatMap((project) =>
		(project.previewImages ?? []).map((image, index) => ({
			id: `${project.id}:${index}`,
			projectId: project.id,
			title: project.title,
			city: project.projectCity,
			designer: project.designerName,
			rooms: project.scopeDetails?.rooms ?? [],
			image,
			aspectRatio: "",
			createdAt: project.createdAt,
		})),
	);
}

/** Distribute items evenly across N columns (left-to-right fill). */
function toColumns<T>(items: readonly T[], n: number): T[][] {
	const cols = Array.from({ length: n }, (): T[] => []);
	items.forEach((item, i) => {
		cols[i % n].push(item);
	});
	return cols;
}

type LoadState =
	| { status: "loading" }
	| { status: "error" }
	| {
			status: "ready";
			projects: PublicProjectSummary[];
			page: number;
			totalPages: number;
			total: number;
			/** Room names of the projects every filter but the room leaves. */
			rooms: string[];
	  };

function parseScope(value: string | null): InspireScope | null {
	return value === "full_home" || value === "partial" ? value : null;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function HomeownerInspirePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const scope = parseScope(searchParams.get("scope"));
	const activeRoom = searchParams.get("room") ?? "All";
	const query = searchParams.get("q") ?? "";
	const view: InspireView =
		searchParams.get("view") === "saved" ? "saved" : "all";

	const [state, setState] = useState<LoadState>({ status: "loading" });
	const [loadingMore, setLoadingMore] = useState(false);
	const [loadMoreFailed, setLoadMoreFailed] = useState(false);
	// What is typed in the box; `query` is what was last submitted.
	const [draft, setDraft] = useState(query);
	// The last search this page put in the address. Another value there came
	// from outside, for example from a link or Back, so the box must show it.
	const writtenQueryRef = useRef(query);
	const generationRef = useRef(0);
	const recent = useRecentSearches();
	const saved = useSavedIdeas();
	const reloadSaved = saved.reload;

	const updateParams = useCallback(
		(changes: Record<string, string | null>) => {
			if ("q" in changes) writtenQueryRef.current = changes.q ?? "";
			setSearchParams(
				(previous) => {
					const next = new URLSearchParams(previous);
					for (const [key, value] of Object.entries(changes)) {
						if (value === null) next.delete(key);
						else next.set(key, value);
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	useEffect(() => {
		if (query === writtenQueryRef.current) return;
		writtenQueryRef.current = query;
		setDraft(query);
	}, [query]);

	// The filters the server applies. Scope, search and room all narrow the
	// whole set of public projects, so a change to any of them starts over.
	const serverFilters = useMemo(
		() => ({
			scope: scope ?? undefined,
			q: query || undefined,
			room: activeRoom === "All" ? undefined : activeRoom,
		}),
		[scope, query, activeRoom],
	);

	const load = useCallback(async () => {
		generationRef.current += 1;
		const generation = generationRef.current;
		setState({ status: "loading" });
		setLoadMoreFailed(false);
		setLoadingMore(false);
		try {
			const result = await fetchPublicProjects({
				limit: PAGE_LIMIT,
				...serverFilters,
			});
			if (generation !== generationRef.current) return;
			setState({
				status: "ready",
				projects: result.projects,
				page: result.page,
				totalPages: result.totalPages,
				total: result.total,
				rooms: result.facets.rooms,
			});
		} catch (err) {
			if (generation !== generationRef.current) return;
			console.error("[Inspire] Failed to load public projects:", err);
			setState({ status: "error" });
		}
	}, [serverFilters]);

	useEffect(() => {
		load();
		return () => {
			generationRef.current += 1;
		};
	}, [load]);

	useEffect(() => {
		if (view === "saved") void reloadSaved();
	}, [view, reloadSaved]);

	const loadMore = useCallback(async () => {
		if (state.status !== "ready" || loadingMore) return;
		const generation = generationRef.current;
		setLoadingMore(true);
		setLoadMoreFailed(false);
		try {
			const result = await fetchPublicProjects({
				limit: PAGE_LIMIT,
				...serverFilters,
				page: state.page + 1,
			});
			if (generation !== generationRef.current) return;
			setState((current) => {
				if (current.status !== "ready") return current;
				const known = new Set(current.projects.map((p) => p.id));
				return {
					...current,
					projects: [
						...current.projects,
						...result.projects.filter((p) => !known.has(p.id)),
					],
					page: result.page,
					totalPages: result.totalPages,
					total: result.total,
					rooms: result.facets.rooms,
				};
			});
		} catch (err) {
			if (generation !== generationRef.current) return;
			console.error("[Inspire] Failed to load more public projects:", err);
			setLoadMoreFailed(true);
		} finally {
			if (generation === generationRef.current) setLoadingMore(false);
		}
	}, [state, loadingMore, serverFilters]);

	const feedProjects = state.status === "ready" ? state.projects : [];
	const hasMore = state.status === "ready" && state.page < state.totalPages;

	const feedCards = useMemo(() => toCards(feedProjects), [feedProjects]);
	const savedCards = useMemo(
		() => toCards([...saved.projects]),
		[saved.projects],
	);
	const shownCards = view === "saved" ? savedCards : feedCards;

	// The server's list of rooms, plus the chosen room when the search leaves no
	// project with it, so the selected room stays visible as a chip.
	const rooms = useMemo(() => {
		const names = state.status === "ready" ? state.rooms : [];
		return activeRoom !== "All" && !names.includes(activeRoom)
			? [...names, activeRoom]
			: names;
	}, [state, activeRoom]);

	const designerCount = useMemo(
		() => new Set(feedCards.map((card) => card.designer).filter(Boolean)).size,
		[feedCards],
	);

	// The feed is filtered by the server. The saved list is complete and not
	// paged, so a typed query narrows it here.
	const filteredCards = useMemo(() => {
		const q = query.trim().toLowerCase();
		const cards =
			view === "saved" && q
				? shownCards.filter((card) =>
						`${card.title} ${card.city ?? ""} ${card.designer ?? ""} ${card.rooms.join(" ")}`
							.toLowerCase()
							.includes(q),
					)
				: shownCards;
		return cards.map((card, index) => ({
			...card,
			aspectRatio: ASPECT_CYCLE[index % ASPECT_CYCLE.length],
		}));
	}, [shownCards, view, query]);

	const handleOpenProject = useCallback(
		(projectId: string) => {
			navigate(`/public-projects/${projectId}`);
		},
		[navigate],
	);

	// The URL query and room at the time a debounce timer fires, not when it
	// started.
	const queryRef = useRef(query);
	queryRef.current = query;
	const roomRef = useRef(activeRoom);
	roomRef.current = activeRoom;
	// The room at the last keystroke. The effect below can start again after
	// the keystroke, so it cannot record this value itself.
	const roomAtKeystrokeRef = useRef(activeRoom);

	// Search when the typing stops. Only a submit records a recent search, so
	// part of a word does not go into the history. A new search clears the
	// room, but not a room that the user chose after the last keystroke.
	useEffect(() => {
		const term = draft.trim();
		if (term === "") return;
		const timer = setTimeout(() => {
			if (term === queryRef.current) return;
			updateParams(
				roomRef.current === roomAtKeystrokeRef.current
					? { q: term, room: null }
					: { q: term },
			);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [draft, updateParams]);

	const handleSearchChange = useCallback(
		(text: string) => {
			roomAtKeystrokeRef.current = roomRef.current;
			setDraft(text);
			if (text.trim() === "" && query !== "") {
				updateParams({ q: null, room: null });
			}
		},
		[query, updateParams],
	);

	const submitSearch = useCallback(
		(text: string) => {
			const term = text.trim();
			if (!term) return;
			setDraft(term);
			recent.record(term);
			updateParams({ q: term, room: null });
		},
		[recent.record, updateParams],
	);

	const toggleSaved = saved.toggle;
	const handleToggleSave = useCallback(
		async (projectId: string) => {
			const outcome = await toggleSaved(projectId);
			if (outcome === "failed") toast.error(t("inspire.saveFailed"));
		},
		[toggleSaved, t],
	);

	const listStatus = view === "saved" ? saved.status : state.status;

	const filtersActive = query !== "" || scope !== null || activeRoom !== "All";
	const clearFilters = useCallback(() => {
		setDraft("");
		updateParams({ q: null, scope: null, room: null });
	}, [updateParams]);

	return (
		<div
			className="h-screen text-dashboard-text bg-dashboard-surface overflow-y-auto pb-20 scrollbar-hide"
			style={{
				background:
					"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)",
			}}
		>
			{/* Hero — the project count is the server's; the photo and designer
			    counts cover the projects loaded so far */}
			<div className="dashboard-container">
				<InspireHero
					photoCount={feedCards.length}
					designerCount={designerCount}
					projectCount={state.status === "ready" ? state.total : 0}
					hasMore={hasMore}
					showCounts={view === "all"}
				/>
			</div>

			{/* Sticky Search and Filter Bar Group */}
			<div className="sticky top-[var(--app-header-h,0px)] z-20 mb-6 flex gap-4 bg-dashboard-surface pb-3">
				<div className="dashboard-container">
					<SearchBar
						value={draft}
						onChange={handleSearchChange}
						recentSearches={recent.searches}
						onSuggestionClick={submitSearch}
						onSearch={submitSearch}
						noContainer
						hideSuggestions
					/>
					<InspireFilterBar
						view={view}
						onViewChange={(next) =>
							updateParams({ view: next === "saved" ? "saved" : null })
						}
						rooms={rooms}
						activeRoom={activeRoom}
						onRoomChange={(room) =>
							updateParams({ room: room === "All" ? null : room })
						}
						activeScope={scope}
						onScopeChange={(next) => updateParams({ scope: next, room: null })}
					/>
				</div>
			</div>

			{view === "all" && saved.status === "error" && (
				<div className="dashboard-container -mt-3 mb-6 flex items-center gap-3 font-body text-xs text-dashboard-muted-dark">
					<span>{t("inspire.savedUnavailable")}</span>
					<button
						type="button"
						className="cursor-pointer underline"
						onClick={() => void reloadSaved()}
					>
						{t("inspire.savedRetry")}
					</button>
				</div>
			)}

			{listStatus === "loading" && (
				<section className="dashboard-container mt-12">
					<SkeletonRows
						label="Loading rooms..."
						rows={2}
						rowClassName="h-64 rounded-lg bg-dashboard-border-soft"
					/>
				</section>
			)}

			{listStatus === "error" && (
				<section className="dashboard-container mt-12">
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper px-6 py-12 text-center">
						<p className="font-body text-sm text-dashboard-muted-dark">
							{view === "saved"
								? t("inspire.savedLoadFailed")
								: "Rooms could not load."}
						</p>
						<StudioButton
							className="mt-4"
							variant="secondary"
							size="sm"
							style={{
								height: 40,
								paddingLeft: 20,
								paddingRight: 20,
								fontSize: 13,
								fontWeight: 600,
								borderRadius: "var(--radius-xl)",
							}}
							onClick={() => (view === "saved" ? void reloadSaved() : load())}
						>
							Retry
						</StudioButton>
					</div>
				</section>
			)}

			{listStatus === "ready" && filteredCards.length === 0 && (
				<section className="dashboard-container mt-12">
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper px-6 py-12 text-center">
						<p className="font-body text-sm text-dashboard-muted-dark">
							{view === "saved" && shownCards.length === 0
								? t("inspire.savedEmpty")
								: view === "all" && filtersActive
									? t("inspire.noMatches")
									: shownCards.length === 0
										? "No public project photos yet. Rooms appear here as Finishd projects go public."
										: "No rooms match this search."}
						</p>
						{view === "all" && filtersActive && (
							<StudioButton
								className="mt-4"
								variant="secondary"
								size="sm"
								style={{
									height: 40,
									paddingLeft: 20,
									paddingRight: 20,
									fontSize: 13,
									fontWeight: 600,
									borderRadius: "var(--radius-xl)",
									border: "1px solid var(--color-dashboard-border)",
								}}
								onClick={clearFilters}
							>
								{t("inspire.clearFilters")}
							</StudioButton>
						)}
						{view === "all" && !filtersActive && shownCards.length === 0 && (
							<StudioButton
								className="mt-4"
								variant="secondary"
								size="sm"
								style={{
									height: 40,
									paddingLeft: 20,
									paddingRight: 20,
									fontSize: 13,
									fontWeight: 600,
									borderRadius: "var(--radius-xl)",
								}}
								onClick={() => navigate("/projects/new")}
							>
								Start your project
							</StudioButton>
						)}
					</div>
				</section>
			)}

			{listStatus === "ready" && filteredCards.length > 0 && (
				<section className="dashboard-container mt-12">
					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 items-start gap-[14px]">
						{toColumns(filteredCards, 4).map((col, colIdx) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: column index is stable
							<div key={colIdx} className="flex flex-col gap-[14px]">
								{col.map((card) => (
									<InspireRoomCard
										key={card.id}
										room={card}
										onOpen={handleOpenProject}
										saved={saved.savedIds.has(card.projectId)}
										savePending={saved.pendingIds.has(card.projectId)}
										onToggleSave={
											saved.status === "ready" ? handleToggleSave : undefined
										}
									/>
								))}
							</div>
						))}
					</div>
				</section>
			)}

			{view === "all" && hasMore && (
				<div className="dashboard-container mt-8 flex flex-col items-center gap-2">
					{loadMoreFailed && (
						<p className="font-body text-sm text-dashboard-muted-dark">
							{t("inspire.loadMoreFailed")}
						</p>
					)}
					<StudioButton
						disabled={loadingMore}
						onClick={() => void loadMore()}
						variant="secondary"
						size="sm"
						style={{
							height: 40,
							paddingLeft: 20,
							paddingRight: 20,
							fontSize: 13,
							fontWeight: 600,
							borderRadius: "var(--radius-xl)",
							border: "1px solid var(--color-dashboard-border)",
							...(loadingMore
								? {
										cursor: "wait",
										boxShadow: "var(--dashboard-shadow-secondary)",
									}
								: {}),
						}}
					>
						{loadingMore
							? t("inspire.loadingMore")
							: loadMoreFailed
								? t("inspire.loadMoreRetry")
								: t("inspire.loadMore")}
					</StudioButton>
				</div>
			)}

			{/* Final CTA — the full projects behind these photos */}
			<section className="dashboard-container mt-16">
				<div className="rounded-[17px] border border-dashboard-warm-300/68 bg-linear-to-b from-dashboard-paper/98 to-dashboard-bg/95 px-6 py-8 text-center shadow-[0_1px_0_white/90_inset,0_12px_28px_-12px_var(--color-dashboard-ink)/18] sm:px-10 sm:py-10">
					<div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-dashboard-warm-100 text-dashboard-accent-light">
						<Sparkles className="h-5 w-5" />
					</div>
					<h2 className="mt-4 font-display text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.05] tracking-tighter text-dashboard-text">
						Every room here is a real project.
					</h2>
					<p className="mx-auto mt-3.5 max-w-xl text-[14px] leading-6 text-dashboard-muted-dark">
						Open the full projects to see budgets, timelines, and the people who
						finished them.
					</p>
					<button
						type="button"
						className="mt-6 inline-flex h-11 items-center gap-2.5 rounded-2xl bg-linear-to-b from-dashboard-accent-light to-dashboard-accent px-5.5 font-body text-[13.5px] font-semibold text-dashboard-on-fill shadow-[inset_0_1px_0_var(--color-dashboard-warm-400)/45,0_6px_18px_-6px_var(--color-dashboard-accent)/55] transition hover:translate-y-px cursor-pointer"
						onClick={() => navigate("/public-projects")}
					>
						Browse all projects
						<ArrowRight className="h-4 w-4" />
					</button>
				</div>
			</section>
		</div>
	);
}
